import { useState, type RefObject, type MouseEvent, type PointerEvent } from 'react';
import { ActionIcon, Loader, Tooltip } from '@mantine/core';
import { IconCamera } from '@tabler/icons-react';
import type { ModelViewerElement } from '@google/model-viewer';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { downloadModelViewerScreenshot } from '@/utils/productPreviewImage';
import { VIEWER_CONTROL_CHROME_STYLE } from './viewerControlChrome';

/** Gap from the reset button (32px) so this sits beside it. */
const BESIDE_RESET_OFFSET_PX = 32 + 8;

const SCREENSHOT_BUTTON_CORNERS = {
  'top-left': { top: 12, left: 12 + BESIDE_RESET_OFFSET_PX },
  'top-right': { top: 12, right: 12 + BESIDE_RESET_OFFSET_PX },
} as const;

type ModelViewerScreenshotButtonProps = {
  modelViewerRef: RefObject<ModelViewerElement | null>;
  /** Must match the neighboring reset button placement. */
  placement?: 'top-left' | 'top-right';
  zIndex?: number;
  /** Optional download basename without extension (e.g. product name). */
  fileName?: string;
};

/**
 * Floating control next to reset-view — downloads the current camera frame as PNG
 * (annotation hotspots composited when visible).
 */
export function ModelViewerScreenshotButton({
  modelViewerRef,
  placement = 'top-left',
  zIndex = 11,
  fileName,
}: ModelViewerScreenshotButtonProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const handleScreenshot = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const mv = modelViewerRef.current;
    if (!mv || busy) return;

    setBusy(true);
    try {
      await downloadModelViewerScreenshot(mv, {
        includeAnnotations: true,
        fileName,
      });
    } catch (error) {
      console.error('Model viewer screenshot failed:', error);
      notifications.show({
        title: t('modelViewer.screenshotErrorTitle'),
        message: t('modelViewer.screenshotError'),
        color: 'red',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tooltip label={t('modelViewer.screenshot')} withArrow position="bottom">
      <ActionIcon
        variant="default"
        radius="xl"
        size={32}
        aria-label={t('modelViewer.screenshot')}
        data-annotation-keep-open
        onClick={handleScreenshot}
        onPointerDown={(event: PointerEvent<HTMLButtonElement>) => event.stopPropagation()}
        disabled={busy}
        style={{
          position: 'absolute',
          ...SCREENSHOT_BUTTON_CORNERS[placement],
          zIndex,
          ...VIEWER_CONTROL_CHROME_STYLE,
        }}
      >
        {busy ? <Loader size={14} color="gray" /> : <IconCamera size={16} stroke={2} />}
      </ActionIcon>
    </Tooltip>
  );
}
